import { useCallback } from "react"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "../components/ui/drawer"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function DrawerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const isOpen = element.variant === "open" || element.visible === true || element.visible === "true"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  // Determine side: "left" or "right", default to "left"
  const side = element.variant === "right" ? "right" : "left"

  const handleClose = useCallback(() => {
    dispatchEvent(element.id, "close", {})
  }, [element.id, dispatchEvent])

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      direction={side}
      data-testid={`drawer-${element.id}`}
    >
      <DrawerContent
        className={cn(
          "h-full w-72 flex flex-col",
          side === "right" ? "ml-auto" : "mr-auto",
          element.className,
        )}
      >
        {/* Header */}
        <DrawerHeader>
          {text && (
            <DrawerTitle>
              {text}
            </DrawerTitle>
          )}
          <DrawerClose
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </DrawerClose>
        </DrawerHeader>
        {/* Navigation items / children */}
        <nav className="flex-1 overflow-y-auto">
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </nav>
      </DrawerContent>
    </Drawer>
  )
}
